import { useEffect, useMemo, useState } from 'preact/hooks';
import { formatCode, get, post } from '../api';
import { Button, Card, Field, Link, Loading, Pill, Tabs, useAction, useLoad } from '../ui';
import { GameSocket } from './socket';
import { Play } from './Play';
import { GMTab } from './GM';
import { Feed } from './Play';
import { StoryGame } from './StoryPlay';

export function GamePage(p: { code: string }) {
	const info = useLoad(() => get(`/api/games/${p.code}`), [p.code]);
	if (info.error) return <main class="page"><Card title="Game not found"><p>{info.error}</p><Link href="/browse">Browse games</Link></Card></main>;
	if (!info.data) return <Loading />;
	const d = info.data;
	if (d.banned) return <main class="page"><Card title="Banned"><p>The GM has banned you from this game.</p></Card></main>;
	if (!d.membership) return <JoinGate data={d} onJoined={info.reload} />;
	if (d.info.kind === 'storygame') return <StoryGame code={p.code} isGm={d.game.isGm} />;
	return <Connected code={p.code} isGm={d.game.isGm} spectator={d.membership === 'spectator'} />;
}

function JoinGate(p: { data: any; onJoined: () => void }) {
	const g = p.data.game;
	const [password, setPassword] = useState('');
	const [pending, setPending] = useState(false);
	const [join, busy] = useAction(async (spectator: boolean) => {
		const r = await post(`/api/games/${g.code}/join`, { password: password || undefined, spectator });
		if (r.status === 'pending') setPending(true);
		else p.onJoined();
	});
	useEffect(() => {
		if (!pending) return;
		const t = setInterval(p.onJoined, 4000);
		return () => clearInterval(t);
	}, [pending]);
	return (
		<main class="page narrow">
			<Card title={g.name}>
				<p class="muted">
					{p.data.info.title} · GM {g.gm} · <code>{formatCode(g.code)}</code>
				</p>
				<p>{p.data.info.synopsis}</p>
				<p class="small">
					{g.seatsTaken}/{g.seats} seats · {g.rating} · {g.timer}s action timer · {g.status === 'active' ? 'in progress — you can join mid-game' : 'in the lobby'}
				</p>
				{p.data.info.claimable.length ? (
					<p class="small">Open characters: {p.data.info.claimable.map((c: any) => `${c.name} (${c.role})`).join(', ')}</p>
				) : null}
				{pending ? (
					<p>
						<Pill tone="accent">Waiting for the GM to let you in…</Pill>
					</p>
				) : (
					<>
						{g.locked ? (
							<Field label="Password">
								<input type="password" value={password} onInput={(e) => setPassword(e.currentTarget.value)} />
							</Field>
						) : null}
						<div class="row">
							<Button onClick={() => join(false)} busy={busy}>
								Join game
							</Button>
							{g.listing === 'public' ? (
								<Button variant="ghost" onClick={() => join(true)} busy={busy}>
									Spectate
								</Button>
							) : null}
						</div>
					</>
				)}
			</Card>
		</main>
	);
}

function Connected(p: { code: string; isGm: boolean; spectator: boolean }) {
	const sock = useMemo(() => new GameSocket(p.code), [p.code]);
	useEffect(() => () => sock.close(), [sock]);
	const [tab, setTab] = useState<'gm' | 'char'>(p.isGm ? 'gm' : 'char');
	const kicked = sock.kicked.use();
	const connected = sock.connected.use();
	const game = sock.game.use();
	if (kicked) return <main class="page narrow"><Card title="Removed"><p>{kicked}</p><Link href="/home">Home</Link></Card></main>;
	if (!game) return <Loading />;
	return (
		<main class="game">
			<div class="game-bar">
				<div class="game-title">
					<strong>{game.name}</strong> <code class="code">{formatCode(game.code)}</code> <Pill tone={game.status === 'active' ? 'ok' : game.status === 'ended' ? 'muted' : 'accent'}>{game.status}</Pill>
					{!connected ? <Pill tone="warn">reconnecting…</Pill> : null}
				</div>
				{p.isGm ? (
					<Tabs
						value={tab}
						onChange={setTab}
						tabs={[
							{ id: 'gm', label: 'Game Master' },
							{ id: 'char', label: 'Character' },
						]}
					/>
				) : null}
			</div>
			{p.spectator ? <Spectator sock={sock} /> : p.isGm && tab === 'gm' ? <GMTab sock={sock} /> : <Play sock={sock} />}
		</main>
	);
}

function Spectator(p: { sock: GameSocket }) {
	const feed = p.sock.feed.use();
	const gameInfo = p.sock.game.use();
	const [area, setArea] = useState('');
	return (
		<div class="play">
			<section class="scene">
				<div class="scene-head">
					<h2>Spectating</h2>
					<input
						placeholder="Follow an area id (blank = all)"
						value={area}
						onChange={(e) => {
							setArea(e.currentTarget.value);
							p.sock.send({ t: 'spectate', area: e.currentTarget.value || null });
						}}
					/>
				</div>
				<p class="muted small">{gameInfo?.synopsis}</p>
				<Feed passages={feed} />
			</section>
		</div>
	);
}
